/**
 * Format a money value for the TY2025 DECIMAL amount fields
 * (USDecimalAmountType / USDecimalAmountNNType — dollars and cents, e.g. "1111.11").
 * Used by every form type EXCEPT 1042-S.
 *
 * NOTE: in the TY2025 schema 1042-S is the lone form that still uses the INTEGER
 * USAmountType / USAmountNNType (whole numbers, no decimal point). A 1042-S A2A
 * generator must use parseIntegerAmount() below — never this function.
 */
export function parseAmount(value?: any): string {
  // Ensure the input is converted to a string first
  const strValue = typeof value === "string" ? value : String(value);

  // Parse as a float and handle NaN cases gracefully
  const amt = parseFloat(strValue) || 0;

  return amt.toFixed(2);
}

/**
 * Format a money value for the IRS INTEGER amount fields (USAmountType /
 * USAmountNNType) — whole numbers, no decimal point. In the TY2025 schema only
 * Form 1042-S uses these. Reserved for a future 1042-S A2A generator (none exists yet).
 */
export function parseIntegerAmount(value?: any): string {
  const strValue = typeof value === "string" ? value : String(value);
  const amt = parseFloat(strValue) || 0;
  return String(Math.round(amt));
}

export function isValidStateCode(state: string): boolean {
  const validStates = new Set([
    "AL",
    "AK",
    "AS",
    "AZ",
    "AR",
    "CA",
    "CO",
    "MP",
    "CT",
    "DE",
    "DC",
    "FM",
    "FL",
    "GA",
    "GU",
    "HI",
    "ID",
    "IL",
    "IN",
    "IA",
    "KS",
    "KY",
    "LA",
    "ME",
    "MH",
    "MD",
    "MA",
    "MI",
    "MN",
    "MS",
    "MO",
    "MT",
    "NE",
    "NV",
    "NH",
    "NJ",
    "NM",
    "NY",
    "NC",
    "ND",
    "OH",
    "OK",
    "OR",
    "PW",
    "PA",
    "PR",
    "RI",
    "SC",
    "SD",
    "TN",
    "TX",
    "VI",
    "UT",
    "VT",
    "VA",
    "WA",
    "WV",
    "WI",
    "WY",
    "AA",
    "AE",
    "AP",
  ]);
  return validStates.has(state);
}

export function getValidCountryCode(country: string): string {
  const validCountryCodes = new Set([
    "AF",
    "AX",
    "AL",
    "AG",
    "AQ",
    "AN",
    "AO",
    "AV",
    "AY",
    "AC",
    "AR",
    "AM",
    "AA",
    "AT",
    "AS",
    "AU",
    "AJ",
    "BF",
    "BA",
    "FQ",
    "BG",
    "BB",
    "BO",
    "BE",
    "BH",
    "BN",
    "BD",
    "BT",
    "BL",
    "BK",
    "BC",
    "BV",
    "BR",
    "IO",
    "VI",
    "BX",
    "BU",
    "UV",
    "BM",
    "BY",
    "CB",
    "CM",
    "CA",
    "CV",
    "CJ",
    "CT",
    "CD",
    "CI",
    "CH",
    "KT",
    "IP",
    "CK",
    "CO",
    "CN",
    "CF",
    "CG",
    "CW",
    "CR",
    "CS",
    "IV",
    "HR",
    "CU",
    "UC",
    "CY",
    "EZ",
    "DA",
    "DX",
    "DJ",
    "DO",
    "DR",
    "TT",
    "EC",
    "EG",
    "ES",
    "EK",
    "ER",
    "EN",
    "ET",
    "FK",
    "FO",
    "FM",
    "FJ",
    "FI",
    "FR",
    "FP",
    "FS",
    "GB",
    "GA",
    "GG",
    "GM",
    "GH",
    "GI",
    "GR",
    "GL",
    "GJ",
    "GQ",
    "GT",
    "GK",
    "GV",
    "PU",
    "GY",
    "HA",
    "HM",
    "VT",
    "HO",
    "HK",
    "HQ",
    "HU",
    "IC",
    "IN",
    "ID",
    "IR",
    "IZ",
    "EI",
    "IS",
    "IT",
    "JM",
    "JN",
    "JA",
    "DQ",
    "JE",
    "JQ",
    "JO",
    "KZ",
    "KE",
    "KQ",
    "KR",
    "KN",
    "KS",
    "KV",
    "KU",
    "KG",
    "LA",
    "LG",
    "LE",
    "LT",
    "LI",
    "LY",
    "LS",
    "LH",
    "LU",
    "MC",
    "MK",
    "MA",
    "MI",
    "MY",
    "MV",
    "ML",
    "MT",
    "IM",
    "RM",
    "MR",
    "MP",
    "MX",
    "MQ",
    "MD",
    "MN",
    "MG",
    "MJ",
    "MH",
    "MO",
    "MZ",
    "WA",
    "NR",
    "BQ",
    "NP",
    "NL",
    "NC",
    "NZ",
    "NU",
    "NG",
    "NI",
    "NE",
    "NF",
    "CQ",
    "NO",
    "MU",
    "OC",
    "PK",
    "PS",
    "LQ",
    "PM",
    "PP",
    "PF",
    "PA",
    "PE",
    "RP",
    "PC",
    "PL",
    "PO",
    "RQ",
    "QA",
    "RO",
    "RS",
    "RW",
    "TB",
    "RN",
    "WS",
    "SM",
    "TP",
    "SA",
    "SG",
    "RI",
    "SE",
    "SL",
    "SN",
    "NN",
    "LO",
    "SI",
    "BP",
    "SO",
    "SF",
    "SX",
    "OD",
    "SP",
    "PG",
    "CE",
    "SH",
    "SC",
    "ST",
    "SB",
    "VC",
    "SU",
    "NS",
    "SV",
    "WZ",
    "SW",
    "SZ",
    "SY",
    "TW",
    "TI",
    "TZ",
    "TH",
    "TO",
    "TL",
    "TN",
    "TD",
    "TS",
    "TU",
    "TX",
    "TK",
    "TV",
    "UG",
    "UP",
    "AE",
    "UK",
    "UY",
    "UZ",
    "NH",
    "VE",
    "VM",
    "VQ",
    "WQ",
    "WF",
    "WI",
    "YM",
    "ZA",
    "ZI",
  ]);
  return validCountryCodes.has(country) && country; // Replace invalid codes with 'XX'
}

function formatDate(inputDate?: string): string {
  if (!inputDate) return "2023-01-01";
  const dateParts = inputDate.match(/(\d{2})\/(\d{2})\/(\d{4})/);
  if (dateParts && dateParts.length === 4) {
    const [_, month, day, year] = dateParts;
    return `${year}-${month}-${day}`;
  }
  return inputDate;
}

export const stateAndTerritories = [
  "AL", "AK", "AS", "AZ", "AR", "CA", "CO", "MP", "CT", "DE", "DC", "FM", "FL",
  "GA", "GU", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MH", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC",
  "ND", "OH", "OK", "OR", "PW", "PA", "PR", "RI", "SC", "SD", "TN", "TX", "VI",
  "UT", "VT", "VA", "WA", "WV", "WI", "WY", "AA", "AE", "AP"
];

export function validatePhoneNumber(value?: string): string {
  if (!value) return "";
  return value.replace(/\D/g, "").substring(0, 30);
}

export function validateTIN(value?: string): string {
  if (!value) return "000000000";
  const cleanTin = value.replace(/\D/g, "");
  return cleanTin.length === 9 ? cleanTin : "000000000";
}

export function validateNameText(value?: string): string {
  if (!value) return "";
  return value
    .replace(/[^A-Za-z0-9#\-\(\)&' ]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .substring(0, 75);
}

export function validateMerchantCategoryCode(value?: string): string | undefined {
  if (!value) return undefined;
  const cleanCode = value.replace(/\D/g, "");
  if (cleanCode.length === 4) return cleanCode;
  return undefined;
}

export function formatAmount(value?: string): string {
  if (!value) return "0";
  const amount = parseFloat(value);
  return isNaN(amount) ? "0" : Math.round(amount).toString();
}

export function convertCheckboxValue(value?: string): string {
  return value === "X" ? "1" : "0";
}